<template>
    <div>
        <div v-if="orgInvitations.length && !fetchingOrgInvitations">
            <div class="d-flex flex-wrap align-center">
                <div v-if="isOrgManager">
                    <OrganizationInvite />
                </div>
                <v-spacer />
                <v-text-field
                    v-model="invitationEmailSearch"
                    autofocus
                    placeholder="Search for an invitation..."
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    hide-details>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch inset dense hide-details class="pa-0 ma-0" v-model="invitationsSwitchData[invitationType]">
                                            <template v-slot:label>
                                                <div>
                                                    <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                    <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="ml-2" v-on="on" @click="refreshOrgInvitations" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>
            <v-data-table
                :headers="invitationHeaders"
                :items="filteredInvitationsList"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                </template>
                <template v-slot:[`item.invitation`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium">{{ item.requestee_email }}</div>
                        <div class="caption mt-1" v-if="item.notification_timestamp">
                            <span class="font-weight-bold">Sent on:</span>
                            {{ item.notification_timestamp | dateTimeToHuman }}
                        </div>
                        <div class="caption mt-1" v-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.REVOKED && item.revocation_timestamp">
                            <span class="font-weight-bold">Revoked on:</span>
                            {{ item.revocation_timestamp | dateTimeToHuman }}
                        </div>
                        <div
                            v-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.ACCEPTED && invitationOrRequestHasTimestamp(item)"
                            class="caption mt-1">
                            <span class="font-weight-bold">Accepted on:</span>
                            {{ item.invitation_responses[0].timestamp | dateTimeToHuman }}
                        </div>
                        <div
                            v-else-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.REJECTED && invitationOrRequestHasTimestamp(item)"
                            class="caption mt-1">
                            <span class="font-weight-bold">Rejected on:</span>
                            {{ item.invitation_responses[0].timestamp | dateTimeToHuman }}
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div v-for="(role, index) in item.roles" :key="index" class="d-flex flex-column">
                            <v-tooltip left>
                                <template v-slot:activator="{ on }">
                                    <div v-if="role.role_type === invitationAndRequestRoleTypes.ORG_ROLE" v-on="on" class="mb-1 d-inline-block">
                                        <v-chip outlined small label>
                                            <v-icon left small>{{ organizationRole(role.Name).icon }}</v-icon>
                                            <span>{{ organizationRole(role.Name).label }}</span>
                                        </v-chip>
                                    </div>
                                </template>
                                <span v-if="role.Name === 'N/A'">The target object has been deleted</span>
                                <span v-else-if="role.method === roleUpdateOptions.OVERWRITE_CURRENT_ROLE">
                                    This role will overwrite the current user role, even if the current role is more powerful that this one.
                                </span>
                                <span v-else-if="role.method === roleUpdateOptions.OVERWRITE_CURRENT_ROLE_IF_LOWER">
                                    This role will overwrite the current role only if the current role is less powerful than this one.
                                </span>
                            </v-tooltip>
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        left
                        offset-x
                        nudge-top="10"
                        v-if="
                            isOrgManager &&
                            (getInvitationStatus(item) === invitationAndRequestStatusTypes.PENDING ||
                                getInvitationStatus(item) === invitationAndRequestStatusTypes.CREATED)
                        ">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('orgStore/fetchOrgInvitations', $route.params.oid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.RESEND_INVITATION"></InvitationActionsDialog>
                            <copy-to-clipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem"></copy-to-clipboard>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('orgStore/fetchOrgInvitations', $route.params.oid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.REVOKE_INVITATION"></InvitationActionsDialog>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <span>No invitations found. Consider inviting new users via the "+ Invite" button above.</span>
                </template>
            </v-data-table>
        </div>
        <div v-else-if="!orgInvitations.length && !fetchingOrgInvitations">
            <v-banner single-line>
                <v-avatar slot="icon" color="white" size="60">
                    <v-icon x-large color="info">mdi-information-outline</v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                    <span class="font-weight-bold secondary--text">No invitations found</span>
                    <p>
                        Currently this organization has no invitation history. Consider inviting new users via the
                        <span class="text-uppercase font-weight-bold secondary--text">
                            <v-icon small color="primary">mdi-plus</v-icon>
                            invite
                        </span>
                        button above.
                    </p>
                </div>
            </v-banner>
        </div>
        <div v-else-if="fetchingOrgInvitations">
            <div
                v-for="item in [
                    { id: 'c1', opacity: 1 },
                    { id: 'c2', opacity: 0.75 },
                    { id: 'c3', opacity: 0.5 }
                ]"
                :key="item.id"
                class="my-6">
                <div :style="{ opacity: item.opacity }">
                    <v-skeleton-loader type="list-item-avatar-two-line" class="mx-auto" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { sortArray, capitalize, organizationRole } from '@/utils'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'
const CopyToClipboard = () => import('@/components/CopyToClipboard')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog')

const createDefaultInvitationsSwitchData = () => ({
    ACCEPTED: true,
    PENDING: true,
    REJECTED: true,
    REVOKED: true,
    EXPIRED: true,
    BROKEN: true,
    CREATED: true
})

export default {
    name: 'OrganizationUserInvitations',
    mixins: [enumsData, invitationsAndRequests],
    components: {
        CopyToClipboard,
        InvitationActionsDialog,
        OrganizationInvite
    },
    methods: {
        capitalize,
        organizationRole,
        resetInvitationFilters() {
            this.invitationsSwitchData = createDefaultInvitationsSwitchData()
        },
        refreshOrgInvitations() {
            this.resetInvitationFilters()
            this.$store.dispatch('orgStore/fetchOrgInvitations', this.$route.params.oid)
        }
    },
    data() {
        return {
            search: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: createDefaultInvitationsSwitchData(),
            invitationEmailSearch: ''
        }
    },
    computed: {
        ...mapState('orgStore', ['orgInvitations', 'fetchingOrgInvitations']),
        ...mapGetters('orgStore', ['isOrgManager']),
        invitationHeaders() {
            const baseHeaders = [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Roles', value: 'roles', sortable: true }
            ]
            if (this.isOrgManager) {
                baseHeaders.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '170px' })
            }
            return baseHeaders
        },
        availableInvitationTypes() {
            const invitationTypes = []
            if (this.orgInvitations && this.orgInvitations.length) {
                this.orgInvitations.forEach(invitation => {
                    const invitationType = this.getInvitationStatus(invitation)
                    if (!invitationTypes.includes(invitationType)) {
                        invitationTypes.push(invitationType)
                    }
                })
                return invitationTypes
            }
            return []
        },
        displayedInvitationTypes() {
            const filteredInvitations = Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
            return filteredInvitations
        },
        filteredInvitationsList() {
            const filterByStatusType = this.orgInvitations.filter(invitation => this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation)))
            if (!this.invitationEmailSearch) {
                const sortedData = sortArray(filterByStatusType, 'invid', 'descending', false)
                return sortedData
            } else {
                const filteredData = filterByStatusType.filter(invitation => invitation.requestee_email.includes(this.invitationEmailSearch.toLowerCase()))
                const sortedData = sortArray(filteredData, 'invid', 'descending', false)
                return sortedData
            }
        }
    }
}
</script>
